// Benepack OMS · 共享外键名称解析层 (fk-name-resolution 切片核心)
// =============================================================
// 诉求: UI 里客户/供应商/产品/SKU/批号/SO/PO/报价 等外键到处显示成数字 ID·体验差。
//   gateway 对业务 DTO 纯透传只带 FK id (无跨服务 join)·但名字"够得着"——靠既有 master/trade
//   list api 拉一次全量·客户端建 id→名 映射 (单租户·参考数据小·完全可行)。
//
// 设计:
//   - 每种 kind 一个 loader: 调对应 list({size:500}) 一次·**模块级 memo 化 Promise**·跨组件共享缓存
//     (同 session 只拉一次·无 react-query 故手写)。
//   - **优雅降级是硬性要求**: 任何 load 失败 (403/网络/缺记录) → 失败缓存空 Map·标 failed·不抛。
//     消费方 (RefName/RefSelect) 回退 #id / 裸 InputNumber·绝不白屏/报错。
//   - useRefNames(kind) hook → { map, loading, ready, failed }·组件挂载触发 load。
//     ★ 命名 useRefNames (非 useRef)·避免与 React.useRef 撞名。
//   - 工具 displayName/fullName(kind, id, map) 按下方"统一显示规则"投影·缺则 #id。
//
// 统一显示规则 (与 executor 提示词表一致):
//   | kind          | list/下拉短显示    | 详情全显示          | 下拉 option label   |
//   | customer      | short_name‖name    | name（short_name）  | short‖name（code）  |
//   | supplier      | short_name‖name    | name（short_name）  | short‖name（code）  |
//   | product       | name               | name                | name（code）        |
//   | sku           | spec               | spec                | code · spec         |
//   | batch         | BatchNo            | BatchNo             | BatchNo             |
//   | salesOrder    | SoNo               | SoNo                | SoNo                |
//   | purchaseOrder | PoNo               | PoNo                | PoNo                |
//   | quotation     | QuotationNo        | QuotationNo         | QuotationNo         |
//   (‖ = 左空则取右·原 #id 一律作 tooltip / 副文本保留可追溯。)
import { useEffect, useState } from 'react';
import { listCustomers, listSuppliers, listProducts, listSkus, listBatches } from '@/api/master';
import { listSalesOrders, listPurchaseOrders, listQuotations } from '@/api/trade';

export type RefKind =
  | 'customer'
  | 'supplier'
  | 'product'
  | 'sku'
  | 'batch'
  | 'salesOrder'
  | 'purchaseOrder'
  | 'quotation';

// 归一记录 (各 kind list item 投影成同款形态·消费方按 kind + 下方工具取显示文本)。
export interface RefRecord {
  id: number;
  primary: string; // 主名/全称: Name / Spec / BatchNo / SoNo / PoNo / QuotationNo
  short: string; // 简称 ("" = 无·仅 customer/supplier 有)
  code: string; // 编码 ("" = 无·document 类无独立 code)
  /** 上游 FK (P2-8 级联过滤用·目前仅 sku=ProductID·其余 kind 不设)。 */
  parentId?: number;
}

interface RefLoadResult {
  map: Map<number, RefRecord>;
  failed: boolean;
}

// 拉满深度·单租户参考数据规模 (客户/供应商/SKU 几十到几百) 一次拉够。
const LOAD_SIZE = 500;

// 各 kind: 拉对应 list → 投影成 RefRecord[]。仅在此处耦合各 list api 字段名 (PascalCase DTO)。
const LOADERS: Record<RefKind, () => Promise<RefRecord[]>> = {
  customer: () =>
    listCustomers({ size: LOAD_SIZE }).then((r) =>
      (r.items ?? []).map((c) => ({
        id: c.ID,
        primary: c.Name,
        short: c.ShortName ?? '',
        code: c.CustomerNo ?? '',
      })),
    ),
  supplier: () =>
    listSuppliers({ size: LOAD_SIZE }).then((r) =>
      (r.items ?? []).map((s) => ({
        id: s.ID,
        primary: s.Name,
        short: s.ShortName ?? '',
        code: s.SupplierNo ?? '',
      })),
    ),
  product: () =>
    listProducts({ size: LOAD_SIZE }).then((r) =>
      (r.items ?? []).map((p) => ({ id: p.ID, primary: p.Name, short: '', code: p.Code ?? '' })),
    ),
  sku: () =>
    listSkus({ size: LOAD_SIZE }).then((r) =>
      (r.items ?? []).map((s) => ({
        id: s.ID,
        primary: s.Spec,
        short: '',
        code: s.Code ?? '',
        // P2-8 级联: SKU 挂产品 FK · SO/PO 行内 sku 下拉按已选产品过滤 (useSkuFilterByProduct)
        parentId: s.ProductID,
      })),
    ),
  batch: () =>
    listBatches({ size: LOAD_SIZE }).then((r) =>
      (r.items ?? []).map((b) => ({ id: b.ID, primary: b.BatchNo, short: '', code: '' })),
    ),
  salesOrder: () =>
    listSalesOrders({ size: LOAD_SIZE }).then((r) =>
      (r.items ?? []).map((o) => ({ id: o.ID, primary: o.SoNo, short: '', code: '' })),
    ),
  purchaseOrder: () =>
    listPurchaseOrders({ size: LOAD_SIZE }).then((r) =>
      (r.items ?? []).map((o) => ({ id: o.ID, primary: o.PoNo, short: '', code: '' })),
    ),
  quotation: () =>
    listQuotations({ size: LOAD_SIZE }).then((r) =>
      (r.items ?? []).map((q) => ({ id: q.ID, primary: q.QuotationNo, short: '', code: '' })),
    ),
};

// 模块级缓存: kind → 进行中/已完成的 Promise (memo 化·同 session 只拉一次)。
const promiseCache = new Map<RefKind, Promise<RefLoadResult>>();
// 已 resolve 的结果同步缓存 (供 hook 初始 state 同步命中·避免页面切换重复 #id 闪烁)。
const resultCache = new Map<RefKind, RefLoadResult>();

// 稳定空 Map 引用 (loading/失败时复用·避免每次 render 产生新引用触发下游 effect 抖动)。
const EMPTY_MAP: Map<number, RefRecord> = new Map();

function toMap(records: RefRecord[]): Map<number, RefRecord> {
  const m = new Map<number, RefRecord>();
  for (const r of records) if (r.id > 0) m.set(r.id, r);
  return m;
}

/**
 * loadRefMap · 拉某 kind 的 id→记录 映射 (模块级 memo·跨组件共享)。
 * **绝不 reject**: 失败 (403/网络/缺记录) 走 catch·缓存空 Map + failed=true (优雅降级硬性要求)。
 */
export function loadRefMap(kind: RefKind): Promise<RefLoadResult> {
  let p = promiseCache.get(kind);
  if (!p) {
    // Promise.resolve().then(loader) 包一层: 即便 loader 同步抛 (如 api 未就绪) 也走 .catch·绝不冒泡。
    p = Promise.resolve()
      .then(() => LOADERS[kind]())
      .then((records): RefLoadResult => ({ map: toMap(records), failed: false }))
      .catch((): RefLoadResult => ({ map: new Map<number, RefRecord>(), failed: true }));
    p.then((res) => resultCache.set(kind, res));
    promiseCache.set(kind, p);
  }
  return p;
}

export interface UseRefNamesResult {
  map: Map<number, RefRecord>;
  loading: boolean;
  ready: boolean;
  failed: boolean;
}

/**
 * useRefNames · 订阅某 kind 的名称映射。组件挂载触发 load (memo 化·同 kind 全局只拉一次)。
 * 返回的 map 引用在 ready 后稳定 (来自共享缓存)·不会引发下游 effect 死循环。
 */
export function useRefNames(kind: RefKind): UseRefNamesResult {
  const [state, setState] = useState<UseRefNamesResult>(() => {
    const cached = resultCache.get(kind);
    return cached
      ? { map: cached.map, loading: false, ready: true, failed: cached.failed }
      : { map: EMPTY_MAP, loading: true, ready: false, failed: false };
  });

  useEffect(() => {
    let cancelled = false;
    const cached = resultCache.get(kind);
    if (cached) {
      setState({ map: cached.map, loading: false, ready: true, failed: cached.failed });
      return;
    }
    setState((s) => (s.ready && !s.loading ? s : { ...s, loading: true }));
    loadRefMap(kind).then((res) => {
      if (!cancelled) {
        setState({ map: res.map, loading: false, ready: true, failed: res.failed });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  return state;
}

// ---- 显示投影工具 (按统一显示规则·消费方与组件复用) ----

/** 列表/下拉短显示: customer/supplier 取 简称‖全称·其余取主名。 */
export function refShortText(kind: RefKind, rec: RefRecord): string {
  if (kind === 'customer' || kind === 'supplier') return rec.short || rec.primary;
  return rec.primary;
}

/** 详情全显示: customer/supplier 全称 +（简称）·其余主名 (简称等于全称或空则不重复)。 */
export function refFullText(kind: RefKind, rec: RefRecord): string {
  if ((kind === 'customer' || kind === 'supplier') && rec.short && rec.short !== rec.primary) {
    return `${rec.primary}（${rec.short}）`;
  }
  return rec.primary;
}

/** 下拉 option label (按统一显示规则表)。 */
export function refOptionLabel(kind: RefKind, rec: RefRecord): string {
  switch (kind) {
    case 'customer':
    case 'supplier': {
      const base = rec.short || rec.primary;
      return rec.code ? `${base}（${rec.code}）` : base;
    }
    case 'product':
      return rec.code ? `${rec.primary}（${rec.code}）` : rec.primary;
    case 'sku':
      return rec.code ? `${rec.code} · ${rec.primary}` : rec.primary;
    default:
      return rec.primary; // batch / salesOrder / purchaseOrder / quotation
  }
}

/** id → 列表/下拉短文本 (缺则 #id·0/空则 —)。 */
export function displayName(kind: RefKind, id: number, map: Map<number, RefRecord>): string {
  if (!id || id <= 0) return '—';
  const rec = map.get(id);
  return rec ? refShortText(kind, rec) : `#${id}`;
}

/** id → 详情全文本 (缺则 #id·0/空则 —)。 */
export function fullName(kind: RefKind, id: number, map: Map<number, RefRecord>): string {
  if (!id || id <= 0) return '—';
  const rec = map.get(id);
  return rec ? refFullText(kind, rec) : `#${id}`;
}
